'use client';

import type { ComponentProps } from 'react';
import { fr } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker } from 'react-day-picker';

import { cn } from '@r/utils/utils.ts';
import { buttonVariants } from '@r/components/ui/button.tsx';

export type CalendarProps = ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  locale = fr,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      locale={locale}
      className={cn('p-3', className)}
      classNames={{
        ...(props.mode === 'range' ? { months: 'flex flex-row gap-4 items-start relative' } : {}),
        month: 'space-y-4',
        month_caption:
          'flex justify-center pt-1 relative items-center max-w-min mx-auto text-nowrap',
        caption_label: 'text-sm font-medium',
        ...(props.mode === 'single'
          ? {
              button_previous: cn(
                buttonVariants({ variant: 'outline' }),
                'h-7 w-7 absolute left-4 bg-transparent p-0'
              ),
              button_next: cn(
                buttonVariants({ variant: 'outline' }),
                'h-7 w-7 absolute right-4 bg-transparent p-0'
              )
            }
          : {
              button_previous: cn(
                buttonVariants({ variant: 'outline' }),
                'h-7 w-7 relative left-0 bg-transparent p-0'
              ),
              button_next: cn(
                buttonVariants({ variant: 'outline' }),
                'h-7 w-7 absolute right-0 bg-transparent p-0'
              )
            }),
        weekdays: 'w-full border-collapse flex',
        weekday: 'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]',
        week: 'flex w-full mt-2',
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-9 w-9 text-center aria-selected:rounded-full text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20'
        ),
        day_button: 'h-9 w-9 p-0 font-normal',
        range_end: 'day-range-end',
        selected: 'bg-primary rounded-md text-primary-foreground',
        today: 'bg-accent text-accent-foreground rounded-md',
        disabled: 'text-muted-foreground opacity-50',
        range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
        hidden: 'invisible',
        nav: 'absolute w-full',
        ...classNames
      }}
      components={{
        Chevron: (props) => {
          if (props.orientation === 'left') {
            return <ChevronLeft className="w-4 h-4" />;
          }
          return <ChevronRight className="w-4 h-4" />;
        }
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
